import { useEffect, useRef } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '@/integrations/supabase/client';
import { captureError } from '@/lib/sentry';

/**
 * Keeps `user_global_stats.last_activity_at` fresh so the Online/Offline
 * indicator and "última actividad" column in /admin-users reflect when the
 * user actually opened the app — not just when they answered a question.
 *
 * Defense in depth (3 layers):
 *   1. Auth events (SIGNED_IN, TOKEN_REFRESHED, INITIAL_SESSION) — ping as
 *      soon as a valid session is confirmed. Captures "app abierta" even if
 *      the user closes before the 30s timer fires.
 *   2. Visibility / unload — ping on visibilitychange and on pagehide
 *      (keepalive) to catch fast iOS background → close cycles.
 *   3. 30s interval while visible — original heartbeat, kept as a safety net.
 *
 * Failures are logged to Sentry (rate-limited) instead of silently swallowed,
 * so we can detect regressions like expired sessions or RLS drift.
 */
export const usePresenceHeartbeat = () => {
  const { user } = useAuth();
  const lastPingAt = useRef<number>(0);
  const lastErrorLoggedAt = useRef<number>(0);

  useEffect(() => {
    if (!user) return;

    let cancelled = false;

    const ping = async (reason: string, opts?: { force?: boolean }) => {
      if (cancelled) return;
      // Throttle: avoid hammering the RPC if multiple triggers fire together
      const now = Date.now();
      if (!opts?.force && now - lastPingAt.current < 5_000) return;
      lastPingAt.current = now;

      try {
        const { error } = await supabase.rpc('touch_my_activity' as any);
        if (error) throw error;
      } catch (err) {
        // Rate-limit Sentry noise: at most one error per hour per session
        if (now - lastErrorLoggedAt.current > 60 * 60 * 1000) {
          lastErrorLoggedAt.current = now;
          captureError(err, {
            flow: 'presence_heartbeat',
            reason,
            userId: user.id,
          });
        }
      }
    };

    // Layer 1 — immediate ping on mount / user change (covers INITIAL_SESSION
    // and SIGNED_IN since this effect re-runs when user.id changes).
    ping('mount_or_signin', { force: true });

    // Layer 1b — listen for TOKEN_REFRESHED (user.id doesn't change, so the
    // effect doesn't re-run on its own).
    const { data: authSub } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'TOKEN_REFRESHED' || event === 'SIGNED_IN') {
        ping(`auth_${event.toLowerCase()}`);
      }
    });

    // Layer 3 — periodic heartbeat while tab is visible
    const interval = setInterval(() => {
      if (!document.hidden) ping('interval');
    }, 30_000);

    // Layer 2 — visibility & unload
    const onVisible = () => {
      if (!document.hidden) ping('visibility');
    };
    const onPageHide = () => {
      // Best-effort fire-and-forget; the keepalive flag lets the request
      // outlive the page on iOS Safari close.
      try {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
        const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;
        if (!supabaseUrl || !anonKey) return;

        // Read the current access token from the persisted session
        let accessToken: string | null = null;
        try {
          const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID as string;
          const raw = localStorage.getItem(`sb-${projectId}-auth-token`);
          if (raw) {
            const parsed = JSON.parse(raw);
            accessToken = parsed?.access_token ?? parsed?.currentSession?.access_token ?? null;
          }
        } catch {
          /* ignore */
        }

        fetch(`${supabaseUrl}/rest/v1/rpc/touch_my_activity`, {
          method: 'POST',
          keepalive: true,
          headers: {
            'Content-Type': 'application/json',
            apikey: anonKey,
            Authorization: `Bearer ${accessToken ?? anonKey}`,
          },
          body: '{}',
        }).catch(() => {});
      } catch {
        // Best-effort only
      }
    };

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('pagehide', onPageHide);

    return () => {
      cancelled = true;
      clearInterval(interval);
      authSub.subscription.unsubscribe();
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, [user?.id]);
};
